import { useEffect, useRef, useState } from "react";

const DURATION = 1400;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Counts the leading number of `value` up from 0 once it scrolls into view,
 * e.g. "20+", "98 %", "4,8 / 5" (Czech decimal comma), "1 rok", or a plain number.
 * Screen readers and reduced-motion users get the final value straight away.
 */
export default function CountUp({ value }) {
  const text = String(value);
  const match = text.match(/^(\d+(?:[.,]\d+)?)(.*)$/s);
  const target = match ? parseFloat(match[1].replace(",", ".")) : null;
  const decimals = match && /[.,]/.test(match[1]) ? match[1].split(/[.,]/)[1].length : 0;
  const suffix = match ? match[2] : "";

  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const animate = target !== null && !reduced;
  const [current, setCurrent] = useState(animate ? 0 : target);
  const ref = useRef(null);

  useEffect(() => {
    if (!animate || !ref.current) return;
    let frame;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / DURATION, 1);
        setCurrent(target * easeOutCubic(t));
        if (t < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    observer.observe(ref.current);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [animate, target]);

  if (target === null) return text;

  const shown = current.toLocaleString("cs-CZ", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return (
    <span ref={ref}>
      <span className="visually-hidden">{text}</span>
      <span aria-hidden="true">{shown}{suffix}</span>
    </span>
  );
}
